import TableRowAction from 'components/common/TableRowAction';
import SoftBadge from 'components/common/SoftBadge';
import CustomAdvanceTable from 'components/common/advance-table/CustomAdvanceTable';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import CloseFacilityModal from 'components/common/modals/CloseFacilityModal';
import dayjs from 'dayjs';
import { parseRouteParams } from 'helpers/utils';
import React, { useState } from 'react';
import { Col, Row, Dropdown } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import paths from 'routes/paths';
import {
  permissionsKeyMap,
  userActions
} from 'routes/permissions/permissionKeys';
import { Can } from 'routes/permissions/Can';
import {
  useSchools,
  useDeleteSchool,
  useCloseFacility,
  useReopenFacility
} from 'hooks/useBeneficiaries';
import useBranchPermission from 'hooks/useBranchPermission';

function SchoolList() {
  const { isMainBranch } = useBranchPermission();
  const schoolColumns = [
    {
      accessor: 'id',
      Header: 'ID',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { id, simple_id } = rowData.row.original;
        return (
          <Link
            to={`/school/${id}/details`}
            className="fw-medium text-1100 hover-primary"
          >
            SCH{simple_id}
          </Link>
        );
      }
    },
    {
      accessor: 'school_name',
      Header: 'School Name',
      cellProps: {
        className: 'py-2'
      }
    },
    {
      accessor: 'students',
      Header: 'Number of students',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData =>
        rowData.row.original.school_students_aggregate?.aggregate?.count ?? 0
    },
    {
      accessor: 'created_at',
      Header: 'Registered date',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { created_at } = rowData.row.original;
        return <>{dayjs(created_at).format('DD-MM-YYYY')}</>;
      }
    },
    {
      accessor: 'school_address',
      Header: 'Address',
      cellProps: {
        className: 'py-2'
      }
    },
    {
      accessor: 'country',
      Header: 'Country',
      cellProps: {
        className: 'py-2'
      }
    },
    // HQ views across branches
    ...(isMainBranch
      ? [
          {
            accessor: 'branch',
            Header: 'Branch',
            cellProps: {
              className: 'py-2'
            },
            Cell: rowData => rowData.row.original.branch?.name || '-'
          }
        ]
      : []),
    {
      accessor: 'closed_at',
      Header: 'Status',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { closed_at, closed_reason } = rowData.row.original;
        return closed_at ? (
          <SoftBadge bg="danger" title={closed_reason || undefined}>
            Closed {dayjs(closed_at).format('DD/MM/YYYY')}
          </SoftBadge>
        ) : (
          <SoftBadge bg="success">Open</SoftBadge>
        );
      }
    },
    {
      accessor: 'school_email',
      Header: 'Email',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { school_email } = rowData.row.original;
        return <a href={`mailto:${school_email}`}>{school_email}</a>;
      }
    },
    {
      accessor: 'school_number',
      Header: 'Phone',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { school_number } = rowData.row.original;
        return <a href={`tel:${school_number}`}>{school_number}</a>;
      }
    },
    {
      accessor: 'id_',
      Header: '',
      cellProps: {
        className: 'py-2 text-end'
      },
      disableSortBy: true,

      Cell: rowData => {
        const { id, closed_at } = rowData.row.original;
        return (
          <TableRowAction
            onCLickEdit={() =>
              navigate(
                parseRouteParams(paths.editSchool, {
                  id
                })
              )
            }
            onCLickDelete={() => handleDeleteClick(id)}
            subject={permissionsKeyMap.facilities.children.school}
          >
            <Can
              I={userActions.update}
              a={permissionsKeyMap.facilities.children.school}
            >
              {closed_at ? (
                <Dropdown.Item onClick={() => reopenFacility(id)}>
                  Reopen
                </Dropdown.Item>
              ) : (
                <Dropdown.Item
                  className="text-warning"
                  onClick={() => handleCloseClick(rowData.row.original)}
                >
                  Close
                </Dropdown.Item>
              )}
            </Can>
          </TableRowAction>
        );
      }
    }
  ];

  const navigate = useNavigate();

  const [selectedId, setSelectedId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [closeTarget, setCloseTarget] = useState(null);

  const { schools, isLoading } = useSchools();
  const { deleteSchoolAsync } = useDeleteSchool();
  const { closeFacilityAsync, isClosing } = useCloseFacility('school');
  const { reopenFacility } = useReopenFacility('school');

  const handleDeleteClick = id => {
    setSelectedId(id);
    setShowDeleteModal(true);
  };

  const handleCloseClick = school => {
    setCloseTarget(school);
  };

  const onConfirmClose = async reason => {
    try {
      await closeFacilityAsync({ id: closeTarget.id, reason });
      setCloseTarget(null);
    } catch (error) {
      console.error('Error closing school:', error);
    }
  };

  const onDelete = async () => {
    try {
      await deleteSchoolAsync(selectedId);
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting school:', error);
    }
  };

  return (
    <>
      <ConfirmationModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        handleConfirm={onDelete}
        title="Delete School"
        body="Are you sure you want to delete this School?"
      />
      <CloseFacilityModal
        show={!!closeTarget}
        kind="school"
        facilityName={closeTarget?.school_name}
        onHide={() => setCloseTarget(null)}
        onConfirm={onConfirmClose}
        isClosing={isClosing}
      />
      <Row>
        <Col md={12} xxl={12} className="h-md-100">
          <CustomAdvanceTable
            data={schools}
            columns={schoolColumns}
            title="Schools"
            addButtonLabel="Add School"
            onClickAddNew={() => {
              navigate(paths.addSchool);
            }}
            loading={isLoading}
            addNew
            addButtonIcon={'book'}
            subject={permissionsKeyMap.facilities.children.school}
          />
        </Col>
      </Row>
    </>
  );
}

export default SchoolList;
